import clsx from 'clsx';
import React from 'react';
import { Modal } from 'react-bootstrap';
import { type Row } from '@tanstack/react-table';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { ExportScopeType } from '@/app/enums/ExportScopeType';
import { type DataTableColumnDef } from '../../../models/DataTableColumnDef';
import { useExportColumns } from '../core/_hooks';
import {
  BACKGROUND_EXPORT_THRESHOLD,
  countLabel,
  DEFAULT_EXPORT_TITLE,
  exportRowLimit,
  exportsByRow,
  MAX_SELECTED_ROWS,
} from '../core/_utils';
import { type BackendExport, type ExportFieldDef, type ExportFormat, type ExportOrientation } from '../types';
import { useBackendExport } from '../hooks/useBackendExport';
import { useExportConfig } from '../hooks/useExportConfig';
import ExportColumnList from './ExportColumnList';
import ExportFilePreview from './ExportFilePreview';
import ExportScopeOptions, { type ExportScopeOption } from './ExportScopeOptions';

interface ExportModalProps {
  show: boolean;
  onHide: () => void;
  columns: DataTableColumnDef<any>[];
  exportFields?: ExportFieldDef[];
  exportColumnKeys?: string[];
  exportColumnLabels?: Record<string, string>;
  exportableFormats: string[];
  currentPageRows: Record<string, any>[];
  selectedRows: Row<object>[];
  /** As linhas marcadas que a tela tem à mão, de qualquer página: é a amostra da prévia em "Selecionados". */
  selectedRowsData?: object[];
  pageTitle?: string;
  pageRange?: string;
  backend?: BackendExport;
}

const BACKEND_FORMATS: ExportFormat[] = ['pdf', 'xlsx'];

const FORMATS: Record<ExportFormat, { label: string; icon: string }> = {
  pdf: { label: 'PDF', icon: 'bi-file-earmark-pdf' },
  csv: { label: 'CSV', icon: 'bi-filetype-csv' },
  xlsx: { label: 'Excel', icon: 'bi-file-earmark-spreadsheet' },
};

const ORIENTATIONS: Record<ExportOrientation, { label: string; icon: string }> = {
  portrait: { label: 'Retrato', icon: 'bi-phone' },
  landscape: { label: 'Paisagem', icon: 'bi-tablet-landscape' },
};

type SegmentedProps<T extends string> = {
  id: string;
  label: string;
  options: T[];
  meta: Record<T, { label: string; icon: string }>;
  value: T;
  onChange: (value: T) => void;
};

const Segmented = <T extends string>({ id, label, options, meta, value, onChange }: SegmentedProps<T>) => (
  <div aria-labelledby={id} role="group">
    <div className="form-label fw-semibold fs-7 text-gray-600" id={id}>
      {label}
    </div>
    <div className="btn-group w-100">
      {options.map((option) => (
        <button
          key={option}
          aria-pressed={value === option}
          className={clsx('btn btn-sm', value === option ? 'btn-primary' : 'btn-light')}
          type="button"
          onClick={() => onChange(option)}
        >
          <i className={clsx('bi me-1', meta[option].icon)} />
          {meta[option].label}
        </button>
      ))}
    </div>
  </div>
);

/**
 * O conteúdo só existe com o modal aberto (o Modal desmonta ao fechar): cada abertura começa da
 * configuração padrão. Fechar no meio de uma exportação do backend não perde nada: a tarefa segue
 * na aba Tarefas do sino.
 */
const ExportModal: React.FC<ExportModalProps> = ({ show, onHide, backend, exportFields, ...props }) => {
  const catalog = useExportColumns(backend?.endpoint, show);

  return (
    <Modal centered show={show} size="xl" onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Exportar Dados</Modal.Title>
      </Modal.Header>
      {backend && !catalog.data ? (
        <Modal.Body>
          {catalog.isError ? (
            <div className="alert alert-danger mb-0">{getErrorMessage(catalog.error)}</div>
          ) : (
            <div className="text-center text-gray-600 py-10">Carregando colunas…</div>
          )}
        </Modal.Body>
      ) : (
        <ExportModalContent
          {...props}
          backend={backend}
          exportFields={backend ? catalog.data?.map(({ key, label, type }) => ({ key, label, type })) : exportFields}
          isOpen={show}
          onHide={onHide}
        />
      )}
    </Modal>
  );
};

const ExportButtonLabel = ({ isWaiting, isLarge }: { isWaiting: boolean; isLarge: boolean }) => {
  if (isWaiting) {
    return (
      <>
        <span className="spinner-border spinner-border-sm me-2" />
        <span>Gerando arquivo…</span>
      </>
    );
  }
  if (isLarge) {
    return (
      <>
        <i className="bi bi-send-fill me-1" />
        <span>Exportar em segundo plano</span>
      </>
    );
  }
  return <>Exportar</>;
};

const selectedDescription = (selectedCount: number, tooManySelected: boolean) => {
  if (tooManySelected) {
    return `Até ${MAX_SELECTED_ROWS.toLocaleString('pt-BR')} linhas marcadas; use Todas as páginas com filtro.`;
  }
  return selectedCount > 0 ? 'Apenas as linhas marcadas na tabela.' : 'Marque linhas na tabela para habilitar.';
};

/**
 * "Quais linhas?" e a que vem marcada. No backend a seleção atravessa páginas e vai como `ids` (com teto); em
 * memória saem as linhas marcadas da tabela.
 */
function scopeOptions(
  backend: BackendExport | undefined,
  currentPageRows: Record<string, unknown>[],
  selectedRowCount: number,
): { options: ExportScopeOption[]; defaultScope: ExportScopeType } {
  const selectedCount = backend ? backend.selectedIds.length : selectedRowCount;
  const tooManySelected = !!backend && selectedCount > MAX_SELECTED_ROWS;
  const canExportSelected = selectedCount > 0 && !tooManySelected;
  const page: ExportScopeOption = {
    value: ExportScopeType.PAGE,
    label: 'Página atual',
    icon: 'bi-file-text',
    description: 'Só as linhas que você está vendo agora.',
    rows: currentPageRows.length,
  };
  const selected: ExportScopeOption = {
    value: ExportScopeType.SELECTED,
    label: 'Selecionados',
    icon: 'bi-check2-square',
    description: selectedDescription(selectedCount, tooManySelected),
    rows: selectedCount,
    disabled: !canExportSelected,
  };
  const defaultScope = canExportSelected ? ExportScopeType.SELECTED : ExportScopeType.PAGE;

  // Tabela em memória não tem outras páginas no servidor para buscar.
  if (!backend) return { options: [page, selected], defaultScope };

  const allPages: ExportScopeOption = {
    value: ExportScopeType.ALL,
    label: 'Todas as páginas',
    icon: 'bi-files',
    description: 'Tudo o que o filtro atual encontra.',
    rows: backend.totalRows,
  };
  if (!exportsByRow(backend, currentPageRows)) return { options: [allPages], defaultScope: ExportScopeType.ALL };

  const { scopes } = backend;
  if (!scopes) return { options: [page, allPages, selected], defaultScope };

  const offered = [page, allPages, selected].filter((option) => scopes.includes(option.value));
  const firstUsable = scopes.find((value) => offered.some((option) => option.value === value && !option.disabled));
  return { options: offered, defaultScope: firstUsable ?? defaultScope };
}

const ExportModalContent: React.FC<Omit<ExportModalProps, 'show'> & { isOpen: boolean }> = ({
  isOpen,
  onHide,
  columns,
  exportFields,
  exportColumnKeys,
  exportColumnLabels,
  exportableFormats: clientFormats,
  currentPageRows,
  selectedRows,
  selectedRowsData,
  pageTitle,
  pageRange,
  backend,
}) => {
  const exportableFormats = (backend ? BACKEND_FORMATS : clientFormats) as ExportFormat[];

  const scopes = scopeOptions(backend, currentPageRows, selectedRows.length);

  const config = useExportConfig({
    columns,
    exportFields,
    exportColumnKeys,
    exportColumnLabels,
    exportableFormats,
    isBackend: !!backend,
    defaultScope: scopes.defaultScope,
    pageTitle,
    pageRange,
  });

  const rows = scopes.options.find((option) => option.value === config.rowScope)?.rows ?? 0;
  // O cache da `DataTable` casa por `id`; tabela em memória sem `id` cai nas linhas marcadas da própria tabela.
  const selectedSample = (
    selectedRowsData?.length ? selectedRowsData : selectedRows.map((row) => row.original)
  ) as Record<string, unknown>[];
  const sampleRows = config.rowScope === ExportScopeType.SELECTED ? selectedSample : currentPageRows;

  // Para o backend toda exportação é tarefa; o limiar decide só se o modal espera ou fecha na hora.
  const isLarge = !!backend && rows > BACKGROUND_EXPORT_THRESHOLD;
  const rowLimit = backend ? exportRowLimit(config.format) : undefined;
  const isOverLimit = rowLimit !== undefined && rows > rowLimit;
  const backendExport = useBackendExport({ endpoint: backend?.endpoint, isLarge, isOpen, onHide });

  const handleExport = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!backend) {
      config.execute(currentPageRows, selectedRows);
      onHide();
      return;
    }
    backendExport.submit(config.toExportRequest(backend), event.currentTarget.getBoundingClientRect());
  };

  const fileFields = config.selectedKeys.flatMap(
    (key) => config.availableFields.find((field) => field.key === key) ?? [],
  );
  const formatLabel = FORMATS[config.format].label;

  return (
    <Modal.Body className="p-0">
      {/* Altura fixa no desktop: o modal não cresce com o catálogo, só a lista de colunas rola. */}
      <div className="row g-0 h-lg-600px">
        <div className="col-lg-6 d-flex flex-column h-lg-100 overflow-lg-auto p-8">
          <ExportScopeOptions options={scopes.options} value={config.rowScope} onChange={config.setRowScope} />
          <ExportColumnList config={config} />
        </div>

        <div className="col-lg-6 d-flex flex-column gap-5 h-lg-100 overflow-lg-auto bg-light rounded-end p-8">
          <div>
            <div className="fs-8 fw-bold text-uppercase text-gray-500 mb-1">Você vai exportar</div>
            <div className="fs-3 fw-bolder text-gray-900">
              {countLabel(rows, 'linha', 'linhas')} · {countLabel(config.selectedKeys.length, 'coluna', 'colunas')}
            </div>
          </div>
          <div className="row g-4">
            <div className="col">
              <Segmented
                id="export-format-title"
                label="Formato"
                meta={FORMATS}
                options={exportableFormats}
                value={config.format}
                onChange={config.setFormat}
              />
            </div>
            {config.format === 'pdf' ? (
              <div className="col">
                <Segmented
                  id="export-orientation-title"
                  label="Orientação"
                  meta={ORIENTATIONS}
                  options={['portrait', 'landscape']}
                  value={config.orientation}
                  onChange={config.setOrientation}
                />
              </div>
            ) : null}
          </div>
          <ExportFilePreview
            fields={fileFields}
            format={config.format}
            headerOf={config.headerOf}
            orientationLabel={config.format === 'pdf' ? ORIENTATIONS[config.orientation].label : undefined}
            sampleRows={sampleRows}
            title={pageTitle ?? DEFAULT_EXPORT_TITLE}
            totalRows={rows}
          />
          {isOverLimit ? (
            <div className="notice d-flex align-items-center bg-light-danger rounded border-danger border border-dashed p-4">
              <i className="bi bi-exclamation-triangle-fill fs-2x text-danger me-4" />
              <div className="fw-semibold text-gray-900">
                {formatLabel} aceita até {rowLimit.toLocaleString('pt-BR')} linhas:{' '}
                {config.format === 'pdf' ? 'use Excel ou afine o filtro.' : 'afine o filtro.'}
              </div>
            </div>
          ) : null}
          {isLarge && !isOverLimit ? (
            <div className="notice d-flex align-items-center bg-light-primary rounded border-primary border border-dashed p-4">
              <i className="bi bi-lightning-charge-fill fs-2x text-primary me-4" />
              <div>
                <div className="fw-bold text-gray-900">Exportação grande: {countLabel(rows, 'linha', 'linhas')}</div>
                <div className="fs-7 text-gray-700">
                  Roda em segundo plano: pode fechar e continuar, avisamos quando terminar.
                </div>
              </div>
            </div>
          ) : null}
          <div className="mt-auto">
            {/* A falha fica no modal (ex.: "afine o filtro"), para ajustar e tentar de novo. */}
            {backendExport.error ? <div className="alert alert-danger">{backendExport.error}</div> : null}
            {backendExport.canContinueInBackground ? (
              <button className="btn btn-light-primary w-100 mb-3" type="button" onClick={onHide}>
                Continuar em segundo plano
              </button>
            ) : null}
            <button
              className="btn btn-primary w-100"
              disabled={config.selectedKeys.length === 0 || rows === 0 || isOverLimit || backendExport.isWaiting}
              type="button"
              onClick={handleExport}
            >
              <ExportButtonLabel isLarge={isLarge} isWaiting={backendExport.isWaiting} />
            </button>
            <button className="btn btn-link btn-color-gray-600 w-100 mt-2" type="button" onClick={onHide}>
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </Modal.Body>
  );
};

export default ExportModal;
